import { Injectable } from '@angular/core';
import { CompanyDataService } from '@core/data/api/company/company-data.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ILocationsState } from './locations-state';
import { Action, Store } from '@ngrx/store';
import { from, mergeMap, switchMap, withLatestFrom } from 'rxjs';
import {
    CreateLocationAction,
    DeleteLocationAction,
    FetchCompanyLocationsAction,
    FetchLocationAction,
    FetchLocationsAction,
    RemoveLocationAction,
    SetCompanyLocationsAction,
    SetLocationsFetching,
    SetLocationAction,
    SetLocationsAction,
    SetViewedLocationAction,
    UpdateLocationAction
} from './locations-actions';
import {
    COMPANY_LOCATIONS_FETCH,
    LOCATIONS_FETCH,
    LOCATION_CREATE,
    LOCATION_DELETE,
    LOCATION_FETCH,
    LOCATION_UPDATE,
    ROUTER_NAVIGATION
} from '../action-types';
import { schema, normalize } from 'normalizr';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { removeObjectKey } from '@core/utils/object';
import { LOCATION_ENTITY, SCHEDULE_ENTITY } from '../entity-types';
import { Schedule } from '@core/models/schedule/schedule';
import { Location } from '@core/models/location/location';
import { getPreference } from '../preference/preference-selectors';
import { getUser } from '../session/session-selectors';
import { User } from '@core/models/user/user';
import { LocationsDataService } from '@core/data/api/location/locations-data.service';
import { SetLocationPreferenceAction, SetMultiLocationPreferenceAction } from '../preference/preference-actions';
import { AuthPolicyService } from '@core/auth/auth-policy.service';
import { sortAlphabeticalByProperty } from '@core/utils/sorting';
import { FEATURE } from '@core/auth/feature-id';
import { IAppRouterState } from '../router/router-state';
import { LOCATIONS_LIST, LOCATIONS_LIST_EDIT, NO_STATE } from '@core/router/app-states';
import { RouterGoAction, RouterNavigationCancelAction } from '../router/router-actions';
import { SessionPreference } from '@core/models/preference/session-preference';
import { getLocations } from './locations-selectors';

@Injectable()
export class LocationEffects {
    constructor(private store$: Store<ILocationsState>,
                private actions$: Actions,
                private authPolicyService: AuthPolicyService,
                private locationsDataService: LocationsDataService,
                private companyDataService: CompanyDataService) {

    }


    fetchLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchLocationsAction>(LOCATIONS_FETCH),
            withLatestFrom(
                this.store$.select(getPreference),
                this.store$.select(getUser)
            ),
            switchMap(([action, preference, user]: [FetchLocationsAction, any, User]) => {
                
                return this.locationsDataService.fetchAllLocations().pipe(
                    mergeMap((res: any) => {
                        // Pre sort locations because we are loading ALL
                        res.locations = res.locations.sort(
                            sortAlphabeticalByProperty('name')
                        );

                        const {
                            normalized: normalizedData,
                            relationships,
                            locationsSchema,
                        } = this.normalizeLocations(res.locations);
                        const actions: any[] = [
                            new SetLocationsFetching(false),
                            new SetLocationsAction({
                                result: normalizedData.result,
                                entities: normalizedData.entities,
                                items: res.locations,
                                meta: res.meta,
                                schema: locationsSchema,
                            })
                        ];

                        // Update multi-location as required
                        if (
                            preference?.company &&
                            !this.authPolicyService.can(FEATURE.LOCATIONS_ALL)
                        ) {
                            const isMultiLocation: boolean =
                                res.locations.length > 1;
                            actions.push(
                                new SetMultiLocationPreferenceAction({
                                    userId: user.id,
                                    companyId: preference.company.id,
                                    isMultiLocation,
                                })
                            );
                        }

                        return actions;
                    }),
                    ossCatchError(([errorActions, errorResponse]) => from(errorActions.concat(
                        [
                            new SetLocationsFetching(false)
                        ]
                    )))
                );
            })
        )
    );


    fetchCompanyLocations$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchCompanyLocationsAction>(COMPANY_LOCATIONS_FETCH),
            switchMap((action: FetchCompanyLocationsAction) => {

                const companyId: string = action.payload.companyId;

                return this.companyDataService.fetchAllCompanyLocations(companyId)
                    .pipe(
                        mergeMap((response) => {

                            const {
                                normalized: normalizedData,
                                relationships,
                                locationsSchema,
                            } = this.normalizeLocations(response.locations);

                            normalizedData.result.forEach((locationId) => {
                                let location =
                                    normalizedData.entities[LOCATION_ENTITY][
                                        locationId
                                    ];
                                location.company_id = action.payload.companyId;
                            });

                            const actions: Action[] = [
                                new SetCompanyLocationsAction({
                                    result: normalizedData.result,
                                    entities: normalizedData.entities,
                                    items: response.locations,
                                    meta: response.meta,
                                    schema: locationsSchema,
                                }),
                                // new SetCompanyLocationIdsAction({
                                //     ids: normalizedData.result,
                                // })
                            ];
                            
                            return actions;
                        }),
                        ossCatchError(([errorActions, error]) =>  from(errorActions))
                    );
            })
        )
    );

    fetchLocation$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchLocationAction>(LOCATION_FETCH),
            switchMap((action: FetchLocationAction) => {

                return this.locationsDataService.fetchLocation(action.payload.locationId)
                    .pipe(
                        mergeMap((res: any) => {
                            const {
                                schedules,
                                location,
                                relationship,
                                locationSchema,
                            } = this.normalizeLocation(res.location);
                            return [
                                new SetLocationAction({
                                    item: location,
                                    schema: locationSchema,
                                })
                            ];
                        })
                    );
            })
        )
    );

    createLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType<CreateLocationAction>(LOCATION_CREATE),
            switchMap((action: CreateLocationAction) => {

                return this.locationsDataService.createLocation(action.payload.request)
                    .pipe(
                        mergeMap((response: any) => {

                            const {
                                location,
                                locationSchema,
                            } = this.normalizeLocation(response.location);

                            return [
                                new SetLocationAction({
                                    item: location,
                                    schema: locationSchema,
                                }),
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error()
                        ].concat(errorActions)))
                    );
            })
        )
    );

    updateLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateLocationAction>(LOCATION_UPDATE),
            switchMap((action: UpdateLocationAction) => {

                return this.locationsDataService.updateLocation(action.payload.locationId, action.payload.request)
                    .pipe(
                        mergeMap((response: any) => {

                            const {
                                location,
                                locationSchema,
                            } = this.normalizeLocation(response.location);

                            return [
                                new SetLocationAction({
                                    item: location,
                                    schema: locationSchema,
                                }),
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error()
                        ].concat(errorActions)))
                    );
            })
        )
    );

    deleteLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType<DeleteLocationAction>(LOCATION_DELETE),
            withLatestFrom(
                this.store$.select(getPreference),
                this.store$.select(getUser),
                this.store$.select(getLocations)
            ),
            switchMap(([action, sessionPreference, user, companyLocations]:
                        [DeleteLocationAction, SessionPreference, User, Location[]]) => {

                const locationId: string = action.payload.locationId;

                return this.locationsDataService.deleteLocation(locationId)
                    .pipe(
                        mergeMap((response: any) => {


                            const actions: Action[] = [
                                new RemoveLocationAction({ id: locationId }),
                                (action as any).success(response)
                            ];


                            if (sessionPreference) {
                                // If deleting the current location need to switch location preference to next available
                                if (
                                    sessionPreference.location &&
                                    sessionPreference.location.id === locationId &&
                                    companyLocations.length > 1
                                ) {
                                    const index: number =
                                        companyLocations[0].id !==
                                        sessionPreference.location.id
                                            ? 0
                                            : 1;
                                    const nextLocation: Location = Object.assign(
                                        new Location(),
                                        companyLocations[index]
                                    );

                                    actions.push(
                                        new SetLocationPreferenceAction({
                                            userId: user.id,
                                            location: nextLocation,
                                        })
                                    );
                                }
    
                                // Update multi-location
                                if (sessionPreference.company) {
                                    const isMultiLocation: boolean =
                                        companyLocations.length - 1 > 1;
                                    actions.push(
                                        new SetMultiLocationPreferenceAction({
                                            userId: user.id,
                                            companyId: sessionPreference.company.id,
                                            isMultiLocation,
                                        })
                                    );
                                }
                            }

                            return actions;
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error()
                        ].concat(errorActions)))
                    );
            })
        )
    );

    routerLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            withLatestFrom(
                this.store$.select(getPreference),
            ),
            mergeMap(([action, userPreference]) => {

                const routerState: IAppRouterState = (action as any).payload
                    .routerState as IAppRouterState;
                const { name: stateName, fromState, params } = routerState;
                const actions: Action[] = [];

                if (stateName === LOCATIONS_LIST_EDIT) {
                    const locationId: string = params.locationId;

                    if (fromState.name === NO_STATE) {
                        actions.push(new RouterNavigationCancelAction({ state: LOCATIONS_LIST_EDIT }));
                        actions.push(new RouterGoAction({ name: LOCATIONS_LIST }));
                    } else {
                        actions.push(new SetViewedLocationAction({ locationId }));
                    }
                }

                return actions;
            })
        )
    );
    
    private normalizeLocations(
        locations: any
    ): {
        normalized: any;
        relationships: { [id: string]: string[] };
        locationsSchema: any;
    } {
        const schedulesSchema: any = [new schema.Entity(SCHEDULE_ENTITY)];
        const locationSchema: any = new schema.Entity(LOCATION_ENTITY, {
            schedules: schedulesSchema,
        });
        const locationsSchema: any = [locationSchema];
        const normalizedData: {
            entities: any;
            result: any;
        } = normalize(locations, locationsSchema);

        // Cast as domain objects
        const locationEntities: any = normalizedData.entities[LOCATION_ENTITY];
        const relationships: any = {};

        if (locationEntities) {
            Object.keys(locationEntities).forEach((key) => {
                relationships[locationEntities[key].id] =
                    locationEntities[key].schedules;

                // we do not want to store schedules: string[] (after normalising) inside location object
                // since we store the relation inside relations anyway and avoid duplicating/maintaining
                // so we remove schedules from location obj
                locationEntities[key] = Object.assign(
                    new Location(),
                    removeObjectKey('schedules', locationEntities[key])
                );
            });

            const scheduleEntities: any =
                normalizedData.entities[SCHEDULE_ENTITY];
            if (scheduleEntities) {
                Object.keys(scheduleEntities).forEach((key) => {
                    scheduleEntities[key] = Object.assign(
                        new Schedule(),
                        scheduleEntities[key]
                    );
                });
            }
        }

        return {
            normalized: normalizedData,
            relationships,
            locationsSchema,
        };
    }

    private normalizeLocation(location: Location): {
        normalized: any;
        schedules: { [id: string]: Schedule };
        location: Location;
        relationship: { [id: string]: string[] };
        locationSchema: any;
    } {
        const schedulesSchema: any = [new schema.Entity(SCHEDULE_ENTITY)];
        const locationSchema: any = new schema.Entity(LOCATION_ENTITY, {
            schedules: schedulesSchema,
        });

        const normalizedData: {
            entities: any;
            result: any;
        } = normalize(location, locationSchema);

        const relationship = {
            [location.id]:
                normalizedData.entities[LOCATION_ENTITY][location.id]
                    .schedules || [],
        };

        // we do not want to store schedules: string[] (after normalising) inside location object
        // since we store the relation inside relations anyway and avoid duplicating/maintaining
        // so we remove schedules from location obj
        const newLocation: Location = Object.assign(
            new Location(),
            removeObjectKey(
                'schedules',
                normalizedData.entities.location[location.id]
            )
        );

        return {
            normalized: normalizedData,
            schedules: normalizedData.entities[SCHEDULE_ENTITY],
            location: newLocation,
            relationship,
            locationSchema,
        };
    }
}